import React, { useState, useRef, useEffect } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface MenuItem {
  title: string;
  description?: string;
  image?: string;
  icon?: IconName;
  link: string;
}

interface MenuSection {
  title?: string;
  cols?: number;
  items: MenuItem[];
}

interface Menu {
  label: string;
  layout?: 'image' | 'icon';
  cols?: number;
  width?: string;
  aspect?: string;
  header?: {
    icon: IconName;
    title: string;
    description: string;
    cta: { label: string; link: string };
  };
  items: MenuItem[];
  sections?: MenuSection[];
  footer?: { text: string; link: string };
}

// ── Icons (Lucide, MIT) ───────────────────────────────────────────────────────
// Keep this list in sync with resources/views/layouts/partials/lucide.blade.php
// in the tailwindcomponents repo.

const ICON_PATHS = {
  sparkles: 'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0zM20 3v4M22 5h-4M4 17v2M5 18H3',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  clapperboard: 'M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3Zm-14 -.7 3.1 3.9m2.9-5.8 3.1 4M3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z',
  'code-2': 'm18 16 4-4-4-4M6 8l-4 4 4 4m8.5-12-5 16',
  code: 'm16 18 6-6-6-6M8 6l-6 6 6 6',
  palette: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z',
  bot: 'M12 8V4H8M2 14h2M20 14h2M15 13v2M9 13v2',
  wrench: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
  'git-compare-arrows': 'M12 6h5a2 2 0 0 1 2 2v7m-4-6-3-3 3-3M12 18H7a2 2 0 0 1-2-2V9m4 6 3 3-3 3',
  keyboard: 'M10 8h.01M12 12h.01M14 8h.01M16 12h.01M18 8h.01M6 8h.01M7 16h10M8 12h.01',
  'book-open': 'M12 7v14M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z',
  'trending-up': 'M16 7h6v6m0-6-8.5 8.5-5-5L2 17',
  'file-text': 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Zm-1 0v4a2 2 0 0 0 2 2h4M10 9H8m8 4H8m8 4H8',
  settings: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
  'bar-chart-3': 'M3 3v18h18M18 17V9M13 17V5M8 17v-3',
  lightbulb: 'M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5M9 18h6M10 22h4',
} as const;

type IconName = keyof typeof ICON_PATHS;

const Icon: React.FC<{ name: IconName; className?: string }> = ({ name, className = 'w-5 h-5' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d={ICON_PATHS[name]} />
    {name === 'bot' && <rect width="16" height="12" x="4" y="8" rx="2" />}
    {name === 'settings' && <circle cx="12" cy="12" r="3" />}
    {name === 'keyboard' && <rect width="20" height="16" x="2" y="4" rx="2" />}
    {name === 'git-compare-arrows' && (
      <>
        <circle cx="5" cy="6" r="3" />
        <circle cx="19" cy="18" r="3" />
      </>
    )}
  </svg>
);

// ── Data ──────────────────────────────────────────────────────────────────────
// Mirrors config/megamenu.php in the tailwindcomponents repo.

const MENUS: Menu[] = [
  {
    label: 'AI Agents',
    layout: 'image',
    cols: 3,
    width: 'w-[700px]',
    items: [
      {
        title: 'AI App Builder',
        description: 'Describe your idea and Creative Tim AI ships a real, deployed app in minutes. Websites, dashboards, slide decks and ad sets, fully editable.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-tim/app-builder-ct-thumb.jpg',
        link: 'https://www.creative-tim.com/ui/build',
      },
      {
        title: 'OpenClaw AI Assistant',
        description: '1-click deploy your personal AI assistant. Integrations, code execution, Claude models, Telegram, cron jobs and more.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-tim/openclaw-thumb.png',
        link: 'https://www.creative-tim.com/ui/openclaw',
      },
      {
        title: 'Hermes Agent',
        description: 'Deploy Hermes Agent by Nous Research. A persistent, autonomous AI agent with built-in memory, tools, and skills.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-tim/hermes-agent-thumb.jpg',
        link: 'https://www.creative-tim.com/ui/hermes',
      },
      {
        title: 'Skills.md and Stacks',
        description: 'Browse and add skills for AI assistants. Enhance your agents with pre-built capabilities.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-tim/skills-thumb.png',
        link: 'https://www.creative-tim.com/ui/skills',
      },
      {
        title: 'Deploy AI Agents for Customer Support',
        description: 'Deploy intelligent AI agents trained on your knowledge base.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/gali/galichat-thumbnail-big.jpg',
        link: 'https://www.galichat.com/',
      },
    ],
    sections: [
      {
        cols: 2,
        items: [
          {
            title: 'Spawn an AI Agent',
            description: 'Pick a ready-made agent, connect your tools, and spawn it in seconds, hosted OpenClaw or Hermes.',
            icon: 'sparkles',
            link: 'https://www.creative-tim.com/ui/spawn-agent',
          },
          {
            title: 'Hire an AI Employee',
            description: 'AI workers that live in Notion, Slack and Gmail and talk to you like colleagues.',
            icon: 'users',
            link: 'https://www.creative-tim.com/ui/hire-ai-employee',
          },
        ],
      },
    ],
  },
  {
    label: 'AI Video',
    layout: 'image',
    cols: 4,
    width: 'w-[820px]',
    aspect: 'aspect-[4/5]',
    header: {
      icon: 'clapperboard',
      title: 'AI Video & Image Generator',
      description: 'Turn one product photo into a full content pack, images and a short video.',
      cta: { label: 'Start creating', link: 'https://www.creative-tim.com/ui/video/ecomm-usecase' },
    },
    items: [
      {
        title: 'Ecommerce content generator',
        description: '5 product images + 1 video from one photo',
        image: 'https://gtmkkedrbdengphuhcxs.supabase.co/storage/v1/object/public/ecomm-content/ecomm/4687/klg9WI-snfeA/image-1.jpg',
        link: 'https://www.creative-tim.com/ui/video/ecomm-usecase',
      },
      {
        title: 'Fashion content generator',
        description: '5 fashion images + 1 video from one photo',
        image: 'https://gtmkkedrbdengphuhcxs.supabase.co/storage/v1/object/public/ecomm-content/ecomm/4687/A44VVL64_LE7/image-1.jpg',
        link: 'https://www.creative-tim.com/ui/video/apparel-usecase',
      },
      {
        title: 'Jewelry & watch content generator',
        description: '5 jewelry images + 1 video from one photo',
        image: 'https://gtmkkedrbdengphuhcxs.supabase.co/storage/v1/object/public/ecomm-content/ecomm/4687/M6lw0cDsS-kX/image-1.png',
        link: 'https://www.creative-tim.com/ui/video/jewelry-usecase',
      },
      {
        title: 'Beauty & skincare content generator',
        description: '5 beauty images + 1 video from one photo',
        image: 'https://gtmkkedrbdengphuhcxs.supabase.co/storage/v1/object/public/ecomm-content/ecomm/4687/p8gu5U3aDJMD/image-1.jpg',
        link: 'https://www.creative-tim.com/ui/video/beauty-usecase',
      },
    ],
  },
  {
    label: 'UI Blocks',
    layout: 'image',
    cols: 3,
    width: 'w-[700px]',
    footer: { text: 'View All UI Blocks →', link: 'https://www.creative-tim.com/ui/blocks' },
    items: [
      {
        title: 'AI Agents UI',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/ai-agents-collection-thumbnail.jpg',
        link: 'https://www.creative-tim.com/ui/blocks/ai-agents',
      },
      {
        title: 'Web3',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/web3-collection-thumbnail.jpg',
        link: 'https://www.creative-tim.com/ui/blocks/web3-collection',
      },
      {
        title: 'Application Admin',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/admin-collection-thumbnail.jpg',
        link: 'https://www.creative-tim.com/ui/blocks/application-admin',
      },
      {
        title: 'Marketing & Presentation',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/marketing-collection-thumbnail.jpg',
        link: 'https://www.creative-tim.com/ui/blocks/marketing-presentation',
      },
      {
        title: 'Content UI',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/content-ui-collection-thumbnail.jpg',
        link: 'https://www.creative-tim.com/ui/blocks/content-ui',
      },
      {
        title: 'Ecommerce UI',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/ecommerce-collection-thumbnail.jpg',
        link: 'https://www.creative-tim.com/ui/blocks/ecommerce-ui',
      },
    ],
  },
  {
    label: 'Products',
    layout: 'image',
    cols: 3,
    width: 'w-[760px]',
    items: [
      {
        title: 'Creative Tim UI — shadcn Blocks',
        description: 'Seamlessly integrate with v0, Lovable, Replit, and other AI platforms.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/shadcn-blocks-black-friday.jpg',
        link: 'https://www.creative-tim.com/ui/',
      },
      {
        title: 'Material Tailwind PRO',
        description: 'Build sleek UIs and complex systems with tools that accelerate your workflow.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/material-tailwind-black-friday.jpg',
        link: 'https://www.material-tailwind.com/v3/pro',
      },
      {
        title: 'Creative Tim Club',
        description: 'Access 130+ premium templates and dashboards for React, Vue, HTML, and more.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/creative-tim-club-black-friday.jpg',
        link: 'https://www.creative-tim.com/club',
      },
      {
        title: 'n8n Workflow Templates',
        description: 'Supercharge your business automation with ready-to-use workflows.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/n8n-workflow-automation-templates.jpg',
        link: 'https://www.creative-tim.com/product/n8n-workflow-templates-collection',
      },
      {
        title: 'Creative AI Pixels',
        description: 'Generate visual assets, illustrations, and marketing materials with AI-powered design tools.',
        image: 'https://raw.githubusercontent.com/creativetimofficial/public-assets/refs/heads/master/creative-ai/creative-ai-pixels-thumb.jpg',
        link: 'https://www.creative-tim.com/ai/pixels',
      },
    ],
    sections: [
      {
        title: 'Services',
        cols: 3,
        items: [
          {
            title: 'Software Development',
            description: 'From prototype to production-ready software, built with clean, scalable code.',
            icon: 'code-2',
            link: 'https://www.creative-tim.com/ui/services#software-development',
          },
          {
            title: 'UI/UX Design',
            description: 'Intuitive, user-first experiences that feel great and are easy to ship.',
            icon: 'palette',
            link: 'https://www.creative-tim.com/ui/services#ui-ux-design',
          },
          {
            title: 'AI Services',
            description: 'Custom AI features, assistants, and automation that help your team move faster.',
            icon: 'bot',
            link: 'https://www.creative-tim.com/ui/services#ai-services',
          },
          {
            title: 'Custom OpenAI/GPT Apps',
            description: 'GPT-powered tools trained on your workflows and data.',
            icon: 'sparkles',
            link: 'https://www.creative-tim.com/ui/services#custom-gpt-apps',
          },
          {
            title: 'From Vibe Coding to Stable Coding',
            description: 'Turn vibe-coded prototypes into stable, scalable products ready for real users.',
            icon: 'wrench',
            link: 'https://www.creative-tim.com/ui/services#vibe-to-stable',
          },
        ],
      },
    ],
  },
  {
    label: 'Learn',
    layout: 'icon',
    cols: 3,
    width: 'w-[720px]',
    items: [
      {
        title: 'AI Tool Comparisons',
        description: 'Claude Code vs Cursor, Seedance vs Sora, agentic vs generative AI, every tool compared side by side.',
        icon: 'git-compare-arrows',
        link: 'https://www.creative-tim.com/ui/compare',
      },
      {
        title: 'AI Tool Cheatsheets',
        description: 'Commands, shortcuts and prompt keywords for Claude Code, Cursor, Codex, Gemini CLI and more.',
        icon: 'keyboard',
        link: 'https://www.creative-tim.com/ui/cheatsheet',
      },
      {
        title: 'Claude Code Guide',
        description: "What it is, install, pricing, tutorials and MCP, the complete guide to Anthropic's agentic coding tool.",
        icon: 'book-open',
        link: 'https://www.creative-tim.com/ui/learn/claude-code',
      },
    ],
  },
  {
    label: 'Skills',
    layout: 'icon',
    cols: 3,
    width: 'w-[760px]',
    footer: { text: 'Browse all Skills & Stacks →', link: 'https://www.creative-tim.com/ui/skills' },
    items: [
      {
        title: 'Growth & Marketing',
        description: 'AI stacks specialized in marketing, SEO, copywriting, analytics, and growth strategies.',
        icon: 'trending-up',
        link: 'https://www.creative-tim.com/ui/skills/growth',
      },
      {
        title: 'Design & UI/UX',
        description: 'AI stacks focused on design systems, UI/UX best practices, branding, and beautiful interfaces.',
        icon: 'palette',
        link: 'https://www.creative-tim.com/ui/skills/design',
      },
      {
        title: 'Development & Engineering',
        description: 'AI stacks specialized in coding, testing, debugging, DevOps, and best practices.',
        icon: 'code',
        link: 'https://www.creative-tim.com/ui/skills/development',
      },
      {
        title: 'Content & Writing',
        description: 'AI stacks for creating, editing, and managing content, documentation, and documents.',
        icon: 'file-text',
        link: 'https://www.creative-tim.com/ui/skills/content',
      },
      {
        title: 'Operations & Workflow',
        description: 'AI stacks for operations, internal workflows, automation, and keeping projects shipping.',
        icon: 'settings',
        link: 'https://www.creative-tim.com/ui/skills/operations',
      },
      {
        title: 'Analytics & Insights',
        description: 'AI stacks for data analysis, metrics, reporting, and turning raw numbers into decisions.',
        icon: 'bar-chart-3',
        link: 'https://www.creative-tim.com/ui/skills/analytics',
      },
      {
        title: 'Research & Discovery',
        description: 'AI stacks for research, exploration, competitive analysis, and synthesizing information.',
        icon: 'lightbulb',
        link: 'https://www.creative-tim.com/ui/skills/research',
      },
    ],
  },
];

// Tailwind needs to see whole class names, so map instead of interpolating.
const COL_CLASS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const CARD_CLASS =
  'group flex flex-col gap-2 rounded-lg border border-white/5 bg-white/5 p-3 transition-all hover:border-white/20 hover:bg-white/10';
const ICON_CARD_CLASS =
  'group flex items-start gap-3 rounded-lg border border-white/5 bg-white/5 p-3 transition-all hover:border-white/20 hover:bg-white/10';

const IconCard: React.FC<{ item: MenuItem }> = ({ item }) => (
  <a href={item.link} target="_blank" rel="noreferrer" className={ICON_CARD_CLASS}>
    <div className="flex items-center justify-center flex-none rounded-lg w-9 h-9 bg-white/10 text-white">
      {item.icon && <Icon name={item.icon} />}
    </div>
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-white">{item.title}</h3>
      <p className="mt-1 text-xs text-white/60">{item.description}</p>
    </div>
  </a>
);

// ── MegaMenu ──────────────────────────────────────────────────────────────────

const MegaMenu: React.FC<{ menu: Menu }> = ({ menu }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isIcon = menu.layout === 'icon';

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-sm font-medium text-white/70 transition-colors hover:text-white focus:outline-none whitespace-nowrap"
      >
        {menu.label}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-40 p-6 mt-2 border shadow-xl bg-black/90 backdrop-blur-sm border-white/10 rounded-2xl">
            <div className={menu.width || 'w-[700px]'}>

              {menu.header && (
                <div className="flex items-center gap-4 pb-4 mb-4 border-b border-white/10">
                  <div className="flex items-center justify-center flex-none text-white rounded-lg w-9 h-9 bg-white/10">
                    <Icon name={menu.header.icon} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-white">{menu.header.title}</h3>
                    <p className="text-xs text-white/60">{menu.header.description}</p>
                  </div>
                  <a
                    href={menu.header.cta.link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center flex-none gap-1.5 px-4 py-1.5 text-sm font-medium text-black transition-colors bg-white rounded-full hover:bg-white/90"
                  >
                    <Icon name="sparkles" className="w-4 h-4" />
                    {menu.header.cta.label}
                  </a>
                </div>
              )}

              <div className={`grid gap-4 ${COL_CLASS[menu.cols || 3]}`}>
                {menu.items.map((item, i) =>
                  isIcon ? (
                    <IconCard key={i} item={item} />
                  ) : (
                    <a key={i} href={item.link} target="_blank" rel="noreferrer" className={CARD_CLASS}>
                      <div className={`${menu.aspect || 'aspect-video'} overflow-hidden rounded-md`}>
                        <img
                          src={item.image}
                          alt={item.title}
                          loading="lazy"
                          className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                        {item.description && <p className="text-xs text-white/60">{item.description}</p>}
                      </div>
                    </a>
                  )
                )}
              </div>

              {menu.sections?.map((section, si) => (
                <div key={si} className="pt-4 mt-4 border-t border-white/10">
                  {section.title && (
                    <h4 className="mb-3 text-xs font-semibold tracking-wide uppercase text-white/40">{section.title}</h4>
                  )}
                  <div className={`grid gap-4 ${COL_CLASS[section.cols || 3]}`}>
                    {section.items.map((item, i) => (
                      <IconCard key={i} item={item} />
                    ))}
                  </div>
                </div>
              ))}

              {menu.footer && (
                <div className="pt-4 mt-4 text-center border-t border-white/10">
                  <a
                    href={menu.footer.link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium transition-colors text-white/70 hover:text-white"
                  >
                    {menu.footer.text}
                  </a>
                </div>
              )}

            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Navbar ────────────────────────────────────────────────────────────────────

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="fixed top-0 z-[100] w-full pt-4">
      <div className="container px-2 mx-auto">
        {/* Pill */}
        <div className="border rounded-full border-white/10 bg-black/80 backdrop-blur-sm">
          <div className="flex items-center justify-between h-12 px-3">

            {/* Left: Logo + separator + desktop nav */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <a href="https://www.creative-tim.com" className="flex items-center gap-2">
                  <img
                    src="https://www.creative-tim.com/ui/apple-touch-icon-square.jpg"
                    alt="Creative Tim"
                    className="w-8 h-8 rounded-full"
                  />
                  <span className="hidden text-sm font-semibold text-white xl:inline">Creative Tim</span>
                </a>
                <div className="hidden w-px h-5 bg-white/20 xl:block" />
                <a href="https://www.creative-tim.com/twcomponents" className="text-sm font-semibold text-white whitespace-nowrap">
                  TW Components
                </a>
              </div>

              <div className="hidden w-px h-6 bg-white/20 xl:block" />

              {/* Desktop nav */}
              <nav className="items-center hidden gap-3 xl:flex 2xl:gap-5">
                {MENUS.map((menu) => (
                  <MegaMenu key={menu.label} menu={menu} />
                ))}
                <a
                  href="https://www.creative-tim.com/twcomponents/components"
                  className="text-sm font-medium transition-colors text-white/70 hover:text-white whitespace-nowrap"
                >
                  Components
                </a>
              </nav>
            </div>

            {/* Right: actions + mobile burger */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.creative-tim.com/twcomponents/discover"
                className="hidden text-sm font-medium transition-colors xl:inline text-white/70 hover:text-white"
              >
                Discover
              </a>
              <a
                href="https://www.creative-tim.com/ui/login"
                className="flex items-center gap-1 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-black transition-colors hover:bg-white/90"
              >
                Login
              </a>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="xl:hidden p-1.5 rounded-full hover:bg-white/10 focus:outline-none"
                aria-label="Toggle menu"
              >
                <svg className="w-5 h-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

          </div>
        </div>

        {/* Mobile menu: every mega-menu becomes a titled section of stacked links */}
        {mobileOpen && (
          <div className="flex flex-col p-4 mt-2 space-y-5 border xl:hidden rounded-2xl border-white/10 bg-black/90 backdrop-blur-sm max-h-[80vh] overflow-y-auto">
            {MENUS.map((menu) => (
              <div key={menu.label} className="space-y-1">
                <p className="px-1 text-xs font-semibold tracking-wide uppercase text-white/40">{menu.label}</p>

                {menu.header && (
                  <a
                    href={menu.header.cta.link}
                    target="_blank"
                    rel="noreferrer"
                    className="block px-1 py-1.5 text-sm font-medium text-white transition-colors hover:text-white/80"
                  >
                    {menu.header.title}
                  </a>
                )}

                {menu.items.map((item, i) => (
                  <a
                    key={i}
                    href={item.link}
                    target="_blank"
                    rel="noreferrer"
                    className="block px-1 py-1.5 text-sm transition-colors text-white/70 hover:text-white"
                  >
                    {item.title}
                  </a>
                ))}

                {menu.sections?.flatMap((s) => s.items).map((item, i) => (
                  <a
                    key={`s${i}`}
                    href={item.link}
                    target="_blank"
                    rel="noreferrer"
                    className="block px-1 py-1.5 text-sm transition-colors text-white/70 hover:text-white"
                  >
                    {item.title}
                  </a>
                ))}

                {menu.footer && (
                  <a
                    href={menu.footer.link}
                    target="_blank"
                    rel="noreferrer"
                    className="block px-1 py-1.5 text-sm font-medium transition-colors text-white/90 hover:text-white"
                  >
                    {menu.footer.text}
                  </a>
                )}
              </div>
            ))}

            <div className="space-y-1">
              <p className="px-1 text-xs font-semibold tracking-wide uppercase text-white/40">Components</p>
              <a href="https://www.creative-tim.com/twcomponents/components" className="block px-1 py-1.5 text-sm transition-colors text-white/70 hover:text-white">All Components</a>
              <a href="https://www.creative-tim.com/twcomponents/discover" className="block px-1 py-1.5 text-sm transition-colors text-white/70 hover:text-white">Discover</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
